import {
  apiDelete,
  apiGet,
  apiGetById,
  apiPost,
  apiPut,
  type ApiResponse,
} from "./genesisApi/httpsMethods";
import type {
  Nda,
  NdaAppliesTo,
  NdaDetail,
  NdaRestrictionType,
} from "../types/nda";

export type NdaWritePayload = {
  person_responsible_id?: number | null;
  person_responsible?: { id: number } | null;
  restriction_type?: NdaRestrictionType | string | null;
  applies_to?: NdaAppliesTo | string | null;
  summary?: string | null;
  expiry_date?: string | null;
  /** When set, creates the NDA and links `deals.nda_id` in one call. */
  deal_id?: number | null;
  deal?: { id: number } | null;
};

function asNdaList(data: unknown): Nda[] {
  if (Array.isArray(data)) return data as Nda[];
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.data)) return record.data as Nda[];
    if (Array.isArray(record.results)) return record.results as Nda[];
  }
  return [];
}

function asNdaDetail(data: unknown): NdaDetail | undefined {
  if (!data || typeof data !== "object") return undefined;
  const record = data as Record<string, unknown>;
  if (typeof record.id === "number") return data as NdaDetail;
  if (record.data && typeof record.data === "object") {
    return record.data as NdaDetail;
  }
  return undefined;
}

export async function listNdas(): Promise<ApiResponse<Nda[]>> {
  const response = await apiGet<unknown>("ndas");
  if (!response.success) {
    return { ...response, data: [] };
  }
  return { ...response, data: asNdaList(response.data) };
}

export async function getNda(id: number): Promise<ApiResponse<NdaDetail>> {
  const response = await apiGetById<unknown>("nda", id);
  if (!response.success) {
    return { ...response, data: undefined };
  }
  return { ...response, data: asNdaDetail(response.data) };
}

export async function createNda(
  payload: NdaWritePayload,
): Promise<ApiResponse<NdaDetail>> {
  const response = await apiPost<unknown>("ndas", payload);
  if (!response.success) {
    return { ...response, data: undefined };
  }
  return { ...response, data: asNdaDetail(response.data) };
}

export async function updateNda(
  id: number,
  payload: NdaWritePayload,
): Promise<ApiResponse<NdaDetail>> {
  const response = await apiPut<unknown>("nda", id, payload);
  if (!response.success) {
    return { ...response, data: undefined };
  }
  return { ...response, data: asNdaDetail(response.data) };
}

/** Soft-deletes the NDA row, expects 204. */
export async function deactivateNda(id: number): Promise<ApiResponse<unknown>> {
  return apiDelete("nda", id);
}
